import { ChangeEvent, useState } from "react";
import { ToggleInput } from "@datagame/ui";
import { useMutation } from "@apollo/client";
import { useTranslation } from "react-i18next";

import { LibraryImage, LibraryImageKind, TriviaQuestion, Video } from "../../../../../../../../../gql/graphql";
import { Section, ImageInput } from "../../../../Common";
import { TooltipWithIcon } from "../../../../../../../../shared/ui/TooltipWithIcon";
import { TRIVIA_QUESTION_UPDATE } from "../../../../../../../../../graphql/mutations/TriviaQuestionMutation";
import { VideoInput } from "../../../../Common/VideoInput";

import styles from "./VisualHelpSection.module.scss";

export interface VisualHelpSectionProps {
  question: TriviaQuestion;
}

export const VisualHelpSection = ({ question }: VisualHelpSectionProps) => {
  const { t: translation } = useTranslation("trivia");
  const t = (key: string) => translation(`advancedSettings.${key}`) as string;
  const [questionImage, setQuestionImage] = useState(question.image);
  const [questionVideo, setQuestionVideo] = useState(question.video);
  const [isUsingVideo, setIsUsingVideo] = useState(Boolean(questionVideo));
  const tKey = isUsingVideo ? "video" : "image";

  const [updateTriviaQuestion] = useMutation(TRIVIA_QUESTION_UPDATE);

  const handleImageChange = (image?: LibraryImage) => {
    setQuestionImage(image);
    updateTriviaQuestion({
      variables: {
        triviaQuestionId: question.id,
        data: {
          imageId: image ? image.id : null,
        },
      },
    });
  };

  const handleVideoChange = (video?: Video) => {
    setQuestionVideo(video);
    updateTriviaQuestion({
      variables: {
        triviaQuestionId: question.id,
        data: {
          videoId: video ? video.id : null,
        },
      },
    });
  };

  const handleIsUsingVideoChange = ({ target: { checked } }: ChangeEvent<HTMLInputElement>) => {
    checked ? handleImageChange() : handleVideoChange();
    setIsUsingVideo(checked);
  };

  return (
    <Section
      className={styles.visualHelpSeciton}
      title={t(`${tKey}.title`)}
      tooltip={<TooltipWithIcon content={t(`${tKey}.tooltip`)} />}
    >
      {isUsingVideo ? (
        <VideoInput onUpload={handleVideoChange} video={questionVideo} onRemove={() => handleVideoChange()} />
      ) : (
        <ImageInput
          onUpload={handleImageChange}
          kind={LibraryImageKind.TriviaImage}
          showRemoveButton={true}
          image={questionImage}
          onRemove={() => handleImageChange()}
        />
      )}
      <ToggleInput
        className={styles.toggle}
        onChange={handleIsUsingVideoChange}
        value={isUsingVideo}
        label={t("video.toggle.label")}
        name="transfer"
        tooltip={<TooltipWithIcon content={t("video.toggle.tooltip")} />}
      />
    </Section>
  );
};
